import { useEffect, useMemo, useState } from 'react';
import { DailyReportTab } from './DailyReportTab';
import { WeeklyReportTab } from './WeeklyReportTab';
import { MonthlyReportTab } from './MonthlyReportTab';
import { QuarterlyReportTab } from './QuarterlyReportTab';
import { YearlyReportTab } from './YearlyReportTab';
import { useAuth } from '../context/AuthContext';

type ReportPeriod = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

const SUB_TABS: { id: ReportPeriod; label: string }[] = [
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'quarterly', label: 'Quarterly' },
  { id: 'yearly', label: 'Yearly' },
];

const VALID_PERIODS: ReportPeriod[] = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'];

const REPORT_TAB_CAPABILITY: Record<ReportPeriod, string> = {
  daily: 'generate_daily',
  weekly: 'generate_weekly',
  monthly: 'generate_monthly',
  quarterly: 'generate_quarterly',
  yearly: 'generate_yearly',
};

function readPeriodFromUrl(): ReportPeriod {
  if (typeof window === 'undefined') return 'daily';
  const raw = new URLSearchParams(window.location.search).get('reportPeriod');
  return (VALID_PERIODS as string[]).includes(raw ?? '')
    ? (raw as ReportPeriod)
    : 'daily';
}

/**
 * Parent "Reports" tab. Renders a sub-tab strip (Daily / Weekly / Monthly / Quarterly / Yearly) and
 * mounts the matching report component below. The active sub-tab is persisted
 * in the URL as `?reportPeriod=daily|weekly|monthly|quarterly|yearly` so it survives page
 * refreshes and works as a deep-link target (combined with the parent
 * `?tab=reports`). Each child manages its own date picker and fetch lifecycle.
 */
export function ReportsTab() {
  const { canCapability } = useAuth();
  const [activeSubTab, setActiveSubTab] = useState<ReportPeriod>(() => readPeriodFromUrl());
  const visibleSubTabs = useMemo(() => SUB_TABS.filter((tab) => canCapability('reports', REPORT_TAB_CAPABILITY[tab.id])), [canCapability]);

  useEffect(() => {
    if (visibleSubTabs.length > 0 && !visibleSubTabs.some((tab) => tab.id === activeSubTab)) {
      setActiveSubTab(visibleSubTabs[0].id);
    }
  }, [activeSubTab, visibleSubTabs]);

  // Mirror the active sub-tab into the URL. `daily` is the default and gets
  // omitted to keep the address bar clean.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const current = url.searchParams.get('reportPeriod');
    if (activeSubTab === 'daily') {
      if (current === null) return;
      url.searchParams.delete('reportPeriod');
    } else {
      if (current === activeSubTab) return;
      url.searchParams.set('reportPeriod', activeSubTab);
    }
    window.history.replaceState({}, '', url.toString());
  }, [activeSubTab]);

  return (
    <div>
      <div
        role="tablist"
        aria-label="Report period"
        className="dr-no-print mb-4 flex gap-1 border-b border-slate-200 dark:border-slate-700"
      >
        {visibleSubTabs.map((t) => {
          const active = activeSubTab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={active}
              onClick={() => setActiveSubTab(t.id)}
              className={[
                'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors -mb-px border-b-2',
                active
                  ? 'border-blue-600 text-blue-600 bg-blue-50 dark:bg-slate-800 dark:text-blue-400'
                  : 'border-transparent text-slate-600 dark:text-slate-300 hover:text-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800',
              ].join(' ')}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {visibleSubTabs.length === 0 && (
        <div className="bg-card border border-border rounded-xl p-6 text-sm text-muted-foreground">
          No report periods are enabled for your role. Contact a Developer to update Permissions Master.
        </div>
      )}

      {activeSubTab === 'daily' && canCapability('reports', 'generate_daily') && <DailyReportTab />}
      {activeSubTab === 'weekly' && canCapability('reports', 'generate_weekly') && <WeeklyReportTab />}
      {activeSubTab === 'monthly' && canCapability('reports', 'generate_monthly') && <MonthlyReportTab />}
      {activeSubTab === 'quarterly' && canCapability('reports', 'generate_quarterly') && <QuarterlyReportTab />}
      {activeSubTab === 'yearly' && canCapability('reports', 'generate_yearly') && <YearlyReportTab />}
    </div>
  );
}
